import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useGroupContext } from "../../hooks/useGroupContext";
import process from "process";
window.process = process;
const API = process.env.REACT_APP_API_URL_NODE || "";

interface TrailLikeItem {
  trailId: string;
  region: string;
  trailName: string;
  from: string;
  start: string;
  count: number;
  mapLink: string;
}

interface GroupedItems {
  [region: string]: TrailLikeItem[];
}

const TrailPreferencesPage: React.FC = () => {
  const { groupId: groupIdFromUrl } = useParams<{ groupId?: string }>();
  const { groupId, isPersonal } = useGroupContext(groupIdFromUrl);
  const navigate = useNavigate();

  const [items, setItems] = useState<TrailLikeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterRegion, setFilterRegion] = useState("");

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ groupId });
    if (filterRegion) params.append("region", filterRegion);

    fetch(`${API}/api/trails/likes/list?${params.toString()}`)
      .then((r) => r.json())
      .then((data) => {
        setItems(data.items || []);
        setLoading(false);
      })
      .catch(() => {
        setError("Errore caricamento preferenze. Riprova.");
        setLoading(false);
      });
  }, [groupId, filterRegion]);

  const byRegion = useMemo<GroupedItems>(() => {
    return items.reduce((acc: GroupedItems, item) => {
      (acc[item.region] = acc[item.region] || []).push(item);
      return acc;
    }, {});
  }, [items]);

  const regions = Object.keys(byRegion).sort();
  const pageTitle = isPersonal
    ? "❤️ I miei Percorsi"
    : "⭐ Preferenze Percorsi";

  const regionLabel = (r: string) =>
    r.charAt(0).toUpperCase() + r.slice(1).replace(/-/g, " ");

  // Nome da mostrare: trailName se significativo, altrimenti "from → start"
  const getTrailLabel = (
    item: TrailLikeItem,
  ): { name: string | null; route: string | null } => {
    console.log("ITEM=", item);
    const hasName = item.trailName && item.trailName !== item.trailId;
    const hasFrom = item.from && item.from.trim();
    const hasStart = item.start && item.start.trim();

    return {
      name: hasName ? item.trailName : null,
      route:
        hasFrom && hasStart
          ? `${item.from} → ${item.start}`
          : hasFrom
            ? item.from
            : hasStart
              ? item.start
              : null,
    };
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "16px 16px 80px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none",
            border: "none",
            fontSize: 24,
            cursor: "pointer",
            padding: "8px",
            minWidth: 44,
            minHeight: 44,
          }}
          aria-label="Torna indietro"
        >
          ←
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{pageTitle}</h1>
          {!isPersonal && (
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>
              Gruppo: <strong>{groupId}</strong>
            </p>
          )}
        </div>
      </div>

      {/* Filtro regione */}
      {regions.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 14, color: "#555", marginRight: 8 }}>
            Filtra per regione:
          </label>
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #ddd",
              fontSize: 14,
            }}
          >
            <option value="">Tutte le regioni</option>
            {regions.map((r) => (
              <option key={r} value={r}>
                {regionLabel(r)}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
          ⏳ Caricamento...
        </div>
      )}

      {error && (
        <div
          style={{
            padding: 16,
            background: "#fff3f3",
            border: "1px solid #ffcdd2",
            borderRadius: 8,
            color: "#c62828",
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div style={{ textAlign: "center", padding: 60, color: "#888" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>
            {isPersonal ? "❤️" : "⭐"}
          </div>
          <p style={{ fontSize: 16 }}>
            {isPersonal
              ? "Non hai ancora salvato nessun percorso."
              : "Nessuna preferenza registrata per questo gruppo."}
          </p>
          <p style={{ fontSize: 14 }}>
            Apri un percorso sulla mappa e clicca{" "}
            <strong>{isPersonal ? "❤️ Salva" : "👍 Like"}</strong>
          </p>
        </div>
      )}

      {/* Sezioni per regione */}
      {!loading &&
        regions.map((region) => (
          <div key={region} style={{ marginBottom: 32 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                borderBottom: "2px solid #4CAF50",
                paddingBottom: 8,
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 18 }}>📍</span>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                {regionLabel(region)}
              </h2>
              <span
                style={{
                  fontSize: 13,
                  color: "#888",
                  fontWeight: "normal",
                  marginLeft: 4,
                }}
              >
                ({byRegion[region].length} percorsi)
              </span>
            </div>

            <div className="preferences-table-wrapper">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f5f5f5" }}>
                    <th style={th}>{isPersonal ? "❤️" : "👍"}</th>
                    <th style={th}>Percorso</th>
                    <th style={th}>Mappa</th>
                  </tr>
                </thead>
                <tbody>
                  {byRegion[region].map((item) => {
                    const { name, route } = getTrailLabel(item);
                    return (
                      <tr
                        key={item.trailId}
                        style={{ borderBottom: "1px solid #eee" }}
                      >
                        {/* Colonna like/count */}
                        <td
                          style={{
                            ...td,
                            fontWeight: 700,
                            textAlign: "center",
                            width: 48,
                          }}
                        >
                          {isPersonal ? "❤️" : item.count}
                        </td>

                        {/* Colonna percorso: nome + from→start + regione */}
                        <td style={td}>
                          {name ? (
                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                              {name}
                            </div>
                          ) : null}
                          {route ? (
                            <div
                              style={{
                                fontSize: 13,
                                color: name ? "#555" : "#222",
                                fontWeight: name ? 400 : 600,
                                marginTop: name ? 2 : 0,
                              }}
                            >
                              🥾 {route}
                            </div>
                          ) : !name ? (
                            <div style={{ fontSize: 13, color: "#aaa" }}>—</div>
                          ) : null}
                          <div
                            style={{
                              display: "inline-block",
                              marginTop: 4,
                              fontSize: 11,
                              color: "#fff",
                              background: "#4CAF50",
                              borderRadius: 10,
                              padding: "2px 8px",
                              fontWeight: 500,
                            }}
                          >
                            {regionLabel(item.region)}
                          </div>
                        </td>

                        {/* Colonna mappa */}
                        <td style={{ ...td, width: 80, textAlign: "center" }}>
                          <Link
                            to={item.mapLink}
                            style={{
                              display: "inline-block",
                              padding: "6px 12px",
                              background: "#4CAF50",
                              color: "white",
                              borderRadius: 6,
                              textDecoration: "none",
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            🗺️ Vai
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      {isPersonal && items.length > 0 && (
        <p
          style={{
            marginTop: 32,
            fontSize: 12,
            color: "#bbb",
            textAlign: "center",
          }}
        >
          I percorsi salvati sono legati a questo browser.
        </p>
      )}
    </div>
  );
};

const th: React.CSSProperties = {
  padding: "8px 12px",
  textAlign: "left",
  fontWeight: 600,
  fontSize: 13,
  color: "#555",
};

const td: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
};

export default TrailPreferencesPage;
